import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Copy, Download } from 'lucide-react';
import { renderToStaticMarkup } from 'react-dom/server';
import htmlTemplate from '../templates/markdownHtmlTemplate';
import './MarkdownExpandModal.css';

function MarkdownExpandModal({ isOpen, onClose, content, timestamp }) {
  const [copied, setCopied] = React.useState(false);

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDownloadHTML = () => {
    const markdownHTML = renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    );
    const dateStr = timestamp ? new Date(timestamp).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const title = `Message - ${dateStr}`;
    const finalHTML = htmlTemplate
      .replace('{{TITLE}}', title)
      .replace('{{CONTENT}}', markdownHTML);

    const blob = new Blob([finalHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `message-${dateStr}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="markdown-expand-overlay" onClick={handleOverlayClick}>
      <div className="markdown-expand-modal">
        <div className="markdown-expand-header">
          <div className="markdown-expand-title">
            <span>Expanded View</span>
            {timestamp && (
              <span className="markdown-expand-timestamp">
                {new Date(timestamp).toLocaleString()}
              </span>
            )}
          </div>
          <div className="markdown-expand-actions">
            <button
              onClick={handleCopy}
              className="markdown-expand-action-button"
              title="Copy to clipboard"
            >
              <Copy size={14} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleDownloadHTML}
              className="markdown-expand-action-button"
              title="Download as HTML"
            >
              <Download size={14} />
              HTML
            </button>
            <button
              onClick={onClose}
              className="markdown-expand-close-button"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="markdown-expand-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export default MarkdownExpandModal;
